import { useEffect, useMemo, useRef, type CSSProperties } from 'react'

/**
 * Aurora — five-blob animated backdrop in ILMU brand colours, with a
 * soft paper veil on top so foreground text retains AA contrast.
 *
 * Extracted from AIPanel so any AI-surface (panel, card, chip, banner)
 * can opt in to the same visual signal without re-implementing the
 * animation system. Mirrors AIPanel's behaviour exactly:
 *
 *   - Five blurred radial-gradient blobs, each anchored to a corner /
 *     centre and offset by a random ±40 px per mount so two side-by-side
 *     Auroras never present the same composition.
 *   - Each blob runs its own WAAPI keyframe path (BLOB_KEYFRAMES below)
 *     at a mutually-prime duration (9, 11, 13, 10, 12 s) so they never
 *     visibly resync.
 *   - On mount each animation is seeded at a *random* point along its
 *     path and paused — when `streaming` first flips true the panel
 *     looks like it's been drifting for a while, not jumping from a
 *     uniform anchor pose.
 *   - When `streaming` flips false the animations decelerate over
 *     3 000 ms via a cubic ease-out tween on `playbackRate` (1 → 0)
 *     and pause wherever they land. Cancelling outright would snap the
 *     blob back to its inline rest transform mid-frame, which looks
 *     jarring.
 *   - All decorative layers are `aria-hidden="true"`. The aurora wrapper
 *     carries `data-ai-halo`-style hooks for callers that wire their
 *     own halo above; here only the blobs are emitted.
 *   - `prefers-reduced-motion: reduce` is respected — both the initial
 *     seed and the streaming effect bail out, so blobs stay at their
 *     inline rest transforms.
 */

interface AuroraProps {
  /**
   * When true, the blobs are actively drifting. When false, any running
   * drift decelerates over 3 s and pauses in place. Default false — the
   * aurora renders as a still composition.
   */
  streaming?: boolean
  /** Override the wrapper's `data-testid`. Default `aurora`. */
  auroraTestId?: string
  /** Override the veil's `data-testid`. Default `aurora-veil`. */
  veilTestId?: string
  /**
   * Tint applied to the paper veil. Lower alpha → blobs read louder.
   * Default `rgba(250, 247, 242, 0.50)` matching AIPanel.
   */
  veilColor?: string
  /**
   * How far the aurora wrapper extends past the parent's border-box on
   * each side. Larger insets let the blur bleed cleanly past the edges
   * without revealing the blur ring. Default -64 (AIPanel value).
   */
  inset?: number
  /**
   * Blob blur radius in px. Default 56 (AIPanel value). Smaller surfaces
   * read better at ~40 — the home AI cards pass 40.
   */
  blur?: number
}

// Blob drift durations (seconds) — mutually prime-ish so blobs never visibly resync.
const BLOB_DURATIONS = [9, 11, 13, 10, 12] as const

// WAAPI keyframe arrays mirroring lms-aurora-drift-1..5 in tokens.css.
// Translations are large (~80–120px) to survive the 56px blur wrapper.
const BLOB_KEYFRAMES: Keyframe[][] = [
  // drift-1
  [
    { transform: 'translate(0, 0) scale(1.00)', offset: 0 },
    { transform: 'translate(120px, -80px) scale(1.25)', offset: 0.25 },
    { transform: 'translate(60px, 100px) scale(0.85)', offset: 0.5 },
    { transform: 'translate(-90px, 40px) scale(1.15)', offset: 0.75 },
    { transform: 'translate(0, 0) scale(1.00)', offset: 1 },
  ],
  // drift-2
  [
    { transform: 'translate(0, 0) scale(1.00)', offset: 0 },
    { transform: 'translate(-110px, 90px) scale(1.18)', offset: 0.2 },
    { transform: 'translate(95px, 60px) scale(0.80)', offset: 0.55 },
    { transform: 'translate(50px, -80px) scale(1.30)', offset: 0.8 },
    { transform: 'translate(0, 0) scale(1.00)', offset: 1 },
  ],
  // drift-3
  [
    { transform: 'translate(0, 0) scale(1.00)', offset: 0 },
    { transform: 'translate(70px, 100px) scale(1.35)', offset: 0.3 },
    { transform: 'translate(-80px, -70px) scale(0.78)', offset: 0.6 },
    { transform: 'translate(-50px, 90px) scale(1.12)', offset: 0.85 },
    { transform: 'translate(0, 0) scale(1.00)', offset: 1 },
  ],
  // drift-4
  [
    { transform: 'translate(0, 0) scale(1.00)', offset: 0 },
    { transform: 'translate(100px, 70px) scale(0.88)', offset: 0.25 },
    { transform: 'translate(-80px, 110px) scale(1.22)', offset: 0.5 },
    { transform: 'translate(70px, -90px) scale(1.08)', offset: 0.75 },
    { transform: 'translate(0, 0) scale(1.00)', offset: 1 },
  ],
  // drift-5
  [
    { transform: 'translate(0, 0) scale(1.00)', offset: 0 },
    { transform: 'translate(-120px, -60px) scale(1.18)', offset: 0.2 },
    { transform: 'translate(60px, -100px) scale(0.85)', offset: 0.5 },
    { transform: 'translate(90px, 80px) scale(1.28)', offset: 0.75 },
    { transform: 'translate(0, 0) scale(1.00)', offset: 1 },
  ],
]

const prefersReducedMotion =
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export function Aurora({
  streaming = false,
  auroraTestId = 'aurora',
  veilTestId = 'aurora-veil',
  veilColor = 'rgba(250, 247, 242, 0.50)',
  inset = -64,
  blur = 56,
}: Readonly<AuroraProps>): JSX.Element {
  const blobRefs = useRef<(HTMLDivElement | null)[]>([])
  const animRefs = useRef<(Animation | null)[]>([])
  const decelRaf = useRef<number | null>(null)

  // Per-mount random rest offsets so two adjacent Auroras never sit on the
  // same composition. Composited on top of the drift transform via `add`.
  const blobOffsets = useMemo(
    () =>
      Array.from({ length: 5 }, () => ({
        x: (Math.random() - 0.5) * 80,
        y: (Math.random() - 0.5) * 80,
      })),
    [],
  )

  // Seed each blob with a paused animation at a random phase on mount.
  useEffect(() => {
    if (prefersReducedMotion) return
    blobRefs.current.forEach((el, i) => {
      if (el == null) return
      if (typeof el.animate !== 'function') return
      if (animRefs.current[i] != null) return
      const durationMs = BLOB_DURATIONS[i]! * 1000
      const anim = el.animate(BLOB_KEYFRAMES[i]!, {
        duration: durationMs,
        iterations: Infinity,
        easing: 'ease-in-out',
        composite: 'add',
      })
      anim.currentTime = Math.random() * durationMs
      anim.pause()
      animRefs.current[i] = anim
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Respond to streaming flips: play on, decelerate on off.
  useEffect(() => {
    if (prefersReducedMotion) return

    if (streaming) {
      if (decelRaf.current != null) {
        cancelAnimationFrame(decelRaf.current)
        decelRaf.current = null
      }
      blobRefs.current.forEach((el, i) => {
        if (el == null) return
        if (typeof el.animate !== 'function') return
        const existing = animRefs.current[i]
        if (existing && existing.playState !== 'finished' && existing.playState !== 'idle') {
          existing.updatePlaybackRate(1)
          if (existing.playState === 'paused') existing.play()
        } else {
          const durationMs = BLOB_DURATIONS[i]! * 1000
          const anim = el.animate(BLOB_KEYFRAMES[i]!, {
            duration: durationMs,
            iterations: Infinity,
            easing: 'ease-in-out',
            composite: 'add',
          })
          anim.currentTime = Math.random() * durationMs
          animRefs.current[i] = anim
        }
      })
      return
    }

    // Cubic ease-out deceleration: playbackRate 1 → 0 over 3 000 ms.
    const DECEL_MS = 3000
    const startTime = performance.now()

    const tick = (now: number): void => {
      const t = Math.min((now - startTime) / DECEL_MS, 1)
      const rate = 1 - t * t * t

      animRefs.current.forEach((anim) => {
        if (anim == null) return
        anim.updatePlaybackRate(rate)
      })

      if (t < 1) {
        decelRaf.current = requestAnimationFrame(tick)
      } else {
        animRefs.current.forEach((anim) => anim?.pause())
        decelRaf.current = null
      }
    }

    decelRaf.current = requestAnimationFrame(tick)
  }, [streaming])

  // Unmount-only cleanup.
  useEffect(() => {
    return () => {
      if (decelRaf.current != null) {
        cancelAnimationFrame(decelRaf.current)
        decelRaf.current = null
      }
      animRefs.current.forEach((anim) => anim?.cancel())
      animRefs.current = []
    }
  }, [])

  const auroraStyle: CSSProperties = {
    position: 'absolute',
    inset,
    filter: `blur(${blur}px)`,
    pointerEvents: 'none',
  }

  const veilStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    background: veilColor,
    pointerEvents: 'none',
  }

  return (
    <>
      <div data-testid={auroraTestId} aria-hidden="true" style={auroraStyle}>
        <div
          data-aurora-blob
          aria-hidden="true"
          ref={(el) => {
            blobRefs.current[0] = el
          }}
          style={{
            position: 'absolute',
            top: '0%',
            left: '0%',
            width: '60%',
            height: '60%',
            background: 'radial-gradient(circle, var(--ilmu-blue) 0%, transparent 65%)',
            opacity: 0.62,
            transform: `translate(${blobOffsets[0]!.x}px, ${blobOffsets[0]!.y}px)`,
            willChange: 'transform',
            pointerEvents: 'none',
          }}
        />
        <div
          data-aurora-blob
          aria-hidden="true"
          ref={(el) => {
            blobRefs.current[1] = el
          }}
          style={{
            position: 'absolute',
            top: '0%',
            right: '0%',
            width: '60%',
            height: '60%',
            background: 'radial-gradient(circle, var(--ilmu-blue-light) 0%, transparent 65%)',
            opacity: 0.62,
            transform: `translate(${blobOffsets[1]!.x}px, ${blobOffsets[1]!.y}px)`,
            willChange: 'transform',
            pointerEvents: 'none',
          }}
        />
        <div
          data-aurora-blob
          aria-hidden="true"
          ref={(el) => {
            blobRefs.current[2] = el
          }}
          style={{
            position: 'absolute',
            top: '25%',
            left: '20%',
            width: '60%',
            height: '60%',
            background: 'radial-gradient(circle, var(--ilmu-ochre) 0%, transparent 65%)',
            opacity: 0.62,
            transform: `translate(${blobOffsets[2]!.x}px, ${blobOffsets[2]!.y}px)`,
            willChange: 'transform',
            pointerEvents: 'none',
          }}
        />
        <div
          data-aurora-blob
          aria-hidden="true"
          ref={(el) => {
            blobRefs.current[3] = el
          }}
          style={{
            position: 'absolute',
            bottom: '0%',
            left: '0%',
            width: '60%',
            height: '60%',
            background: 'radial-gradient(circle, var(--ilmu-blue-dark) 0%, transparent 65%)',
            opacity: 0.62,
            transform: `translate(${blobOffsets[3]!.x}px, ${blobOffsets[3]!.y}px)`,
            willChange: 'transform',
            pointerEvents: 'none',
          }}
        />
        <div
          data-aurora-blob
          aria-hidden="true"
          ref={(el) => {
            blobRefs.current[4] = el
          }}
          style={{
            position: 'absolute',
            bottom: '0%',
            right: '0%',
            width: '60%',
            height: '60%',
            background: 'radial-gradient(circle, var(--ilmu-blue) 0%, transparent 65%)',
            opacity: 0.62,
            transform: `translate(${blobOffsets[4]!.x}px, ${blobOffsets[4]!.y}px)`,
            willChange: 'transform',
            pointerEvents: 'none',
          }}
        />
      </div>
      <div data-testid={veilTestId} aria-hidden="true" style={veilStyle} />
    </>
  )
}
